import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

export type SiteRole = 'USER' | 'SITE_ADMIN';

export interface PublicUser {
  id: number;
  email: string;
  displayName: string;
  plan: string;
  siteRole: SiteRole;
  emailVerifiedAt: string | null;
}

interface LoginResponse {
  accessToken: string;
  user: PublicUser;
}

interface RefreshResponse {
  accessToken: string;
}

/**
 * Access token lives only in memory (never localStorage/sessionStorage) to
 * keep it out of reach of an XSS payload. The refresh token is an httpOnly
 * cookie the browser sends automatically (withCredentials) and this service
 * never touches directly — silent refresh is how a page reload gets a new
 * access token.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  private readonly accessTokenSig = signal<string | null>(null);
  private readonly userSig = signal<PublicUser | null>(null);
  private readonly initializingSig = signal(true);
  private refreshInFlight: Promise<string> | null = null;

  readonly isAuthenticated = computed(() => this.accessTokenSig() !== null);
  readonly user = this.userSig.asReadonly();
  readonly initializing = this.initializingSig.asReadonly();

  getAccessToken(): string | null {
    return this.accessTokenSig();
  }

  register(input: {
    email: string;
    password: string;
    displayName: string;
    acceptTerms: boolean;
  }) {
    return firstValueFrom(
      this.http.post<{ message: string }>(
        `${environment.apiBaseUrl}/auth/register`,
        input,
      ),
    );
  }

  async login(input: { email: string; password: string }): Promise<PublicUser> {
    const res = await firstValueFrom(
      this.http.post<LoginResponse>(
        `${environment.apiBaseUrl}/auth/login`,
        input,
        { withCredentials: true },
      ),
    );
    this.accessTokenSig.set(res.accessToken);
    this.userSig.set(res.user);
    return res.user;
  }

  /**
   * Never rejects — the backend call is best-effort (the server-side
   * `logout` endpoint is @Public() and only acts on the refresh cookie,
   * so it should basically never fail, but a network error is still
   * possible). Local session state is cleared regardless, so a caller
   * (e.g. the header's logout button) can always safely proceed to
   * navigate away without needing its own try/catch.
   */
  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.apiBaseUrl}/auth/logout`,
          {},
          { withCredentials: true },
        ),
      );
    } catch {
      // Best-effort — see docblock above.
    } finally {
      this.clearSession();
    }
  }

  resendVerification(email: string) {
    return firstValueFrom(
      this.http.post<{ message: string }>(
        `${environment.apiBaseUrl}/auth/resend-verification`,
        { email },
      ),
    );
  }

  requestPasswordReset(email: string) {
    return firstValueFrom(
      this.http.post<{ message: string }>(
        `${environment.apiBaseUrl}/auth/request-password-reset`,
        { email },
      ),
    );
  }

  resetPassword(input: { token: string; newPassword: string }) {
    return firstValueFrom(
      this.http.post<{ message: string }>(
        `${environment.apiBaseUrl}/auth/reset-password`,
        input,
      ),
    );
  }

  verifyEmail(token: string) {
    return firstValueFrom(
      this.http.get<{ message: string }>(
        `${environment.apiBaseUrl}/auth/verify-email`,
        { params: { token } },
      ),
    );
  }

  /** Called once at app bootstrap — tries to turn the refresh cookie (if any) into a session. */
  async tryRestoreSession(): Promise<void> {
    try {
      const token = await this.refresh();
      const me = await firstValueFrom(
        this.http.get<PublicUser>(`${environment.apiBaseUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      );
      this.userSig.set(me);
    } catch {
      this.clearSession();
    } finally {
      this.initializingSig.set(false);
    }
  }

  /**
   * Used by the interceptor (and bootstrap) to mint a new access token from
   * the refresh cookie. Refresh tokens are single-use and rotate server-side,
   * so if several requests 401 around the same moment (e.g. a page firing
   * a few parallel API calls right as the access token expires) and each
   * called this independently, only the first POST /auth/refresh would see
   * a still-valid cookie — the rest would race against an already-rotated
   * token, which the backend treats as replay and responds to by revoking
   * the whole session. Coalescing into one shared in-flight promise means
   * concurrent callers all await the same request and get the same new
   * token, instead of each firing their own.
   */
  async refresh(): Promise<string> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.performRefresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<string> {
    const res = await firstValueFrom(
      this.http.post<RefreshResponse>(
        `${environment.apiBaseUrl}/auth/refresh`,
        {},
        { withCredentials: true },
      ),
    );
    this.accessTokenSig.set(res.accessToken);
    return res.accessToken;
  }

  clearSession(): void {
    this.accessTokenSig.set(null);
    this.userSig.set(null);
  }
}
